import React from "react";
import { Form } from "@components";
import { Wrapper } from "../wrapper";

export type RiskNetFormValues = {
  region: string | undefined;
  level: string | undefined;
  risknetID: string;
};

type Key = keyof RiskNetFormValues;

type Props = {
  onCancel: () => void;
  onSubmit: (values: RiskNetFormValues) => void;
  open: boolean;
  values: Partial<RiskNetFormValues>;
  readonly?: boolean;
};

export const RiskNetForm: React.FC<Props> = ({
  onCancel,
  onSubmit,
  open,
  values,
  readonly,
}) => {
  const form = Form.useManager<RiskNetFormValues>({
    region: "",
    level: "",
    risknetID: "",
    ...values,
  });

  function onFormValueChange(name: keyof RiskNetFormValues, value: any): void {
    form.set(Form.target.VALUE, [name, value]);
    validate([name, value]).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });
  }

  function validate([name, value]: [string, any]): Promise<[Key, Error?]> {
    switch (name) {
      case "region":
      case "level":
      case "risknetID":
        return Promise.resolve([name as Key, Form.Validators.hasValue(value)]);
      default:
        return Promise.resolve([name as Key]);
    }
  }

  function onFormSubmit(): void {
    Promise.all(Object.entries(form.values).map(validate)).then(
      (validations) => {
        const valid = validations.reduce((valid, [name, error]) => {
          return valid && !error;
        }, true);

        if (valid) {
          onSubmit(form.values);
        } else {
          validations.forEach(([name, error]) => {
            form.set(Form.target.ERROR, [name, error]);
          });
        }
      }
    );
  }

  return (
    <Wrapper
      onCancel={() => {
        onCancel();
        if (values) {
          form.set(Form.target.VALUES, values);
        } else {
          form.reset();
        }
      }}
      onSubmit={onFormSubmit}
      open={open}
      title="RiskNet"
      readonly={readonly}
    >
      <Form>
        <Form.Title>1 - Information</Form.Title>
        <Form.Dropdown
          label="Region(s)"
          required
          onChange={onFormValueChange}
          options={[
            "AP (RDP G1A1PRSNI0001)",
            "NA (RDP G1NWPRSNI001)",
            "UK (RDP G1EWPRSNI001)",
            "AP-NA",
            "AP-UK",
            "NA-UK",
            "AP-NA-UK",
          ]}
          name="region"
          size={12}
          error={form.errors.region}
          value={form.values.region || ""}
        />
        <Form.Dropdown
          label="Level of Access"
          required
          onChange={onFormValueChange}
          options={[
            "User",
            "Administrator",
            "Support",
            "User Support",
            "System Administrator",
          ]}
          name="level"
          size={12}
          error={form.errors.level}
          value={form.values.level || ""}
        />
        <Form.Textarea
          error={form.errors.risknetID}
          label="RiskNet ID"
          maxLength={50}
          required
          onChange={onFormValueChange}
          name="risknetID"
          placeholder="Write details here. Max 50 characters"
          rows={5}
          rowsMax={5}
          size={12}
          value={form.values.risknetID}
        />
      </Form>
    </Wrapper>
  );
};
